/**
 * NameProjectWizard — standalone modal wrapping `NameProjectFields`.
 *
 * Two modes:
 *   • mode="create"  — assigns a new Project Number and collects label +
 *                       descriptor. Used when a caller doesn't have its
 *                       own form (simple "+ New Project" buttons).
 *   • mode="edit"    — rename an existing project. Project Number is shown
 *                       read-only; ClientLabel and Descriptor are editable.
 *
 * Embed-mode users (QualifyLeadModal, ConvertToDealModal, etc.) should use
 * `<NameProjectFields>` directly rather than this wrapper modal.
 */

import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { NameProjectFields, NameProjectFieldsValue, reserveProjectNumberOnSubmit } from './NameProjectFields';
import { isProjectNumber } from '../../utils/projectName';
import './NameProjectWizard.css';

interface NameProjectWizardProps {
  mode: 'create' | 'edit';
  initialValue: NameProjectFieldsValue;
  onConfirm: (value: NameProjectFieldsValue) => Promise<void> | void;
  onClose: () => void;
  /** Modal title. Defaults: "Name this project" / "Rename project". */
  title?: string;
  /** Optional warning shown above the actions (used in rename mode). */
  warningMessage?: string;
  /** When true in create mode, skip counter reservation until design fee is paid. */
  deferProjectNumber?: boolean;
  /** When true in edit mode without a number, preview and reserve on submit. */
  assignProjectNumberOnSubmit?: boolean;
  /** Show “Assign project number” on the edit modal when no number exists. */
  allowAssignProjectNumber?: boolean;
  /** Copy on the deferred-number row (under-contract jobs, etc.). */
  assignNumberHint?: string;
  /** Disables the confirm button until truthy. Defaults to allowing submit
   *  once label+descriptor are non-empty (project number optional when deferred). */
  isSubmittable?: (value: NameProjectFieldsValue) => boolean;
  /** Confirm button label. */
  confirmLabel?: string;
  /** Optional fields rendered after the naming fields (e.g. designer / architect). */
  afterFields?: React.ReactNode;
  /** Wider, denser layout for Project Hub “edit project” (naming + site + team). */
  layout?: 'default' | 'compact';
}

const defaultIsSubmittable = (
  v: NameProjectFieldsValue,
  opts?: { deferProjectNumber?: boolean; assignProjectNumberOnSubmit?: boolean },
) => {
  const numberOk =
    opts?.deferProjectNumber
    || opts?.assignProjectNumberOnSubmit
    || Boolean(v.projectNumber.trim());
  return numberOk && Boolean(v.clientLabel.trim() || v.descriptor.trim());
};

export const NameProjectWizard: React.FC<NameProjectWizardProps> = ({
  mode,
  initialValue,
  onConfirm,
  onClose,
  title,
  warningMessage,
  deferProjectNumber = false,
  assignProjectNumberOnSubmit = false,
  allowAssignProjectNumber = false,
  assignNumberHint,
  isSubmittable,
  confirmLabel,
  afterFields,
  layout = 'default',
}) => {
  const compact = layout === 'compact';
  const [value, setValue] = useState<NameProjectFieldsValue>(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assigningNumber, setAssigningNumber] = useState(assignProjectNumberOnSubmit);

  const assignOnSubmit = assignProjectNumberOnSubmit || assigningNumber;
  const deferNumber = deferProjectNumber && !assignOnSubmit;
  const missingNumber = !isProjectNumber(initialValue.projectNumber);
  const showAssignButton = allowAssignProjectNumber && mode === 'edit' && missingNumber && !assignOnSubmit;

  const computedTitle = title
    || (assignOnSubmit && missingNumber ? 'Assign project number' : undefined)
    || (mode === 'create' ? 'Name this project' : 'Rename project');
  const computedConfirm = confirmLabel
    || (assignOnSubmit && missingNumber ? 'Assign number' : undefined)
    || (mode === 'create' ? 'Create project' : 'Save changes');
  const submittableFn = isSubmittable
    ?? ((v: NameProjectFieldsValue) => defaultIsSubmittable(v, {
      deferProjectNumber: deferNumber,
      assignProjectNumberOnSubmit: assignOnSubmit,
    }));
  const submittable = submittableFn(value) && !saving;
  const assignWarning = assignOnSubmit && missingNumber
    ? 'This reserves the next project number and updates Project P&L, files, calendar, and the client portal label. The number cannot be changed later.'
    : warningMessage;

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      let toSubmit = value;
      const shouldReserveOnSubmit =
        (mode === 'create' && !deferNumber)
        || (assignOnSubmit && missingNumber);
      if (shouldReserveOnSubmit) {
        const reservedProjectNumber = await reserveProjectNumberOnSubmit();
        toSubmit = { ...value, projectNumber: reservedProjectNumber };
        setValue(toSubmit);
      }
      await onConfirm(toSubmit);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="npw-overlay" onClick={onClose}>
      <div
        className={`npw-modal${compact ? ' npw-modal--compact' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="npw-header">
          <h2 className="npw-title">{computedTitle}</h2>
          <button className="npw-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="npw-body">
          {compact ? (
            <div className="npw-compact-panel">
              <NameProjectFields
                mode={mode}
                value={value}
                onChange={setValue}
                onError={setError}
                disabled={saving}
                deferProjectNumber={deferNumber}
                assignProjectNumberOnSubmit={assignOnSubmit}
                assignNumberHint={assignNumberHint}
                onAssignNumber={showAssignButton ? () => setAssigningNumber(true) : undefined}
                compact
              />
              {afterFields}
            </div>
          ) : (
            <>
              <NameProjectFields
                mode={mode}
                value={value}
                onChange={setValue}
                onError={setError}
                disabled={saving}
                deferProjectNumber={deferNumber}
                assignProjectNumberOnSubmit={assignOnSubmit}
                assignNumberHint={assignNumberHint}
                onAssignNumber={showAssignButton ? () => setAssigningNumber(true) : undefined}
              />
              {afterFields}
            </>
          )}

          {assignWarning && (
            <div className={`npw-warning${compact ? ' npw-warning--compact' : ''}`}>
              <AlertTriangle size={14} />
              <span>{assignWarning}</span>
            </div>
          )}

          {error && <div className="npw-error">{error}</div>}
        </div>

        <div className="npw-footer">
          <button className="npw-btn npw-btn-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="npw-btn npw-btn-primary"
            onClick={handleConfirm}
            disabled={!submittable}
          >
            {saving ? 'Saving…' : computedConfirm}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NameProjectWizard;
